<template>
  <v-dialog
    v-model="localShow"
    max-width="800px"
    scrollable
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="status-history-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="dialog-header">
        <div class="d-flex align-center">
          <v-icon
            icon="mdi-history"
            size="24"
            class="mr-3"
          ></v-icon>
          <div>
            <h2 class="dialog-title">
              Historial de Estados
            </h2>
            <p class="dialog-subtitle mb-0">
              Incidencia #{{ incident?.id }} - {{ incident?.title }}
            </p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="handleClose"
        ></v-btn>
      </v-card-title>

      <!-- Contenido del diálogo -->
      <v-card-text class="pa-0" style="max-height: 600px;">
        <div class="history-container">
          <!-- Información de la incidencia -->
          <v-alert
            type="info"
            variant="tonal"
            class="ma-4 mb-4"
          >
            <div class="incident-summary">
              <div class="incident-title">
                <strong>{{ incident?.title }}</strong>
              </div>
              <div class="incident-current-status">
                Estado actual: 
                <v-chip
                  :color="getStatusColor(currentStatus)"
                  variant="tonal"
                  class="ml-2"
                >
                  <v-icon 
                    :icon="getStatusIcon(currentStatus)" 
                    size="16" 
                    class="mr-1"
                  ></v-icon>
                  {{ getStatusText(currentStatus) }}
                </v-chip>
              </div>
            </div>
          </v-alert>

          <!-- Estadísticas del historial -->
          <div class="history-stats ma-4 mb-4">
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-card flat class="stat-card">
                  <v-card-text class="text-center pa-4">
                    <div class="stat-number text-primary">{{ historyStats.totalChanges }}</div>
                    <div class="stat-label">Cambios Totales</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card flat class="stat-card">
                  <v-card-text class="text-center pa-4">
                    <div class="stat-number text-success">{{ historyStats.uniqueUsers }}</div>
                    <div class="stat-label">Usuarios Involucrados</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card flat class="stat-card">
                  <v-card-text class="text-center pa-4">
                    <div class="stat-number text-info">{{ historyStats.avgTimeInStatus }}</div>
                    <div class="stat-label">Tiempo Promedio</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card flat class="stat-card">
                  <v-card-text class="text-center pa-4">
                    <div class="stat-number text-warning">{{ historyStats.totalDuration }}</div>
                    <div class="stat-label">Duración Total</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Línea de tiempo del historial -->
          <div class="history-timeline pa-4">
            <h3 class="timeline-title mb-4">
              <v-icon icon="mdi-timeline" class="mr-2"></v-icon>
              Línea de Tiempo Completa
            </h3>
            
            <v-timeline 
              side="end" 
              density="default"
              line-color="primary"
              class="custom-timeline"
            >
              <v-timeline-item
                v-for="(entry, index) in sortedHistory"
                :key="entry.id"
                :dot-color="getStatusColor(entry.status)"
                size="large"
                class="timeline-item"
              >
                <template v-slot:icon>
                  <v-icon
                    :icon="getStatusIcon(entry.status)"
                    size="20"
                    color="white"
                  ></v-icon>
                </template>
                
                <div class="timeline-card">
                  <v-card elevation="3" class="status-card">
                    <v-card-text class="pa-4">
                      <div class="status-header">
                        <div class="status-change">
                          <v-chip
                            :color="getStatusColor(entry.status)"
                            variant="tonal"
                            class="status-chip"
                          >
                            <v-icon 
                              :icon="getStatusIcon(entry.status)" 
                              size="16" 
                              class="mr-1"
                            ></v-icon>
                            {{ getStatusText(entry.status) }}
                          </v-chip>
                          
                          <!-- Indicador de cambio -->
                          <div v-if="index > 0" class="status-transition">
                            <v-icon icon="mdi-arrow-left" size="16" class="mx-2" color="grey"></v-icon>
                            <v-chip
                              :color="getStatusColor(sortedHistory[index - 1].status)"
                              size="small"
                              variant="outlined"
                            >
                              {{ getStatusText(sortedHistory[index - 1].status) }}
                            </v-chip>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Información del usuario -->
                      <div class="user-info">
                        <v-avatar size="32" class="mr-3">
                          <v-icon icon="mdi-account" size="18"></v-icon>
                        </v-avatar>
                        <div>
                          <div class="user-name">{{ entry.userName }}</div>
                          <div class="change-time">{{ formatDateTime(entry.timestamp) }}</div>
                        </div>
                      </div>
                      
                      <!-- Comentario del cambio -->
                      <div v-if="entry.comment" class="status-comment">
                        <v-icon icon="mdi-comment-text" size="16" class="mr-2" color="grey"></v-icon>
                        <span class="comment-text">"{{ entry.comment }}"</span>
                      </div>
                      
                      <!-- Información adicional -->
                      <div class="status-metadata">
                        <div class="metadata-item">
                          <v-icon icon="mdi-clock" size="14" class="mr-1"></v-icon>
                          <span class="metadata-text">{{ getTimeFromNow(entry.timestamp) }}</span>
                        </div>
                        
                        <div v-if="entry.automatic" class="metadata-item">
                          <v-icon icon="mdi-robot" size="14" class="mr-1" color="info"></v-icon>
                          <span class="metadata-text">Cambio automático</span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-timeline-item>
            </v-timeline>
          </div>

          <!-- Resumen de tiempos por estado -->
          <div class="time-summary ma-4">
            <v-card flat>
              <v-card-title class="text-h6 pb-2">
                <v-icon icon="mdi-chart-pie" class="mr-2"></v-icon>
                Tiempo en Cada Estado
              </v-card-title>
              <v-card-text>
                <div class="time-breakdown">
                  <div
                    v-for="summary in timeSummary"
                    :key="summary.status"
                    class="time-item"
                  >
                    <div class="time-status">
                      <v-chip
                        :color="getStatusColor(summary.status)"
                        size="small"
                        variant="tonal"
                      >
                        {{ getStatusText(summary.status) }}
                      </v-chip>
                    </div>
                    <div class="time-duration">{{ summary.duration }}</div>
                    <div class="time-percentage">
                      <v-progress-linear
                        :model-value="summary.percentage"
                        :color="getStatusColor(summary.status)"
                        height="6"
                        rounded
                      ></v-progress-linear>
                      <span class="percentage-text">{{ summary.percentage.toFixed(1) }}%</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-card-text>

      <!-- Acciones del diálogo -->
      <!--
        P13-V5A-R5 · RETIRADOS los dos controles inertes de esta barra de acciones.
        "Exportar Historial" (mdi-download) y "Imprimir" (mdi-printer) prometían producir un
        documento del historial de estados de la incidencia. Sus handlers eran, literalmente,
        un console.log más un comentario TODO ('Implementar exportación a PDF/Excel',
        'Implementar impresión'): al pulsarlos no se generaba ni se descargaba nada, ni había
        endpoint de exportación de historial detrás. Esto es el RASTRO DE AUDITORÍA de una
        incidencia — exactamente el artefacto que alguien pediría para sustentar una disputa —,
        de modo que un botón que aparenta emitirlo y no lo emite es una afirmación de negocio
        falsa. No se implementa la exportación aquí: se retira la promesa. La exportación real
        y autorizada del producto es la de reportes organizacionales
        (/api/reports/organizational/export/{excel,pdf}/:reportType).
        El historial en pantalla sigue siendo el persistido y leído del backend; "Cerrar" se
        mantiene porque funciona.
      -->
      <v-card-actions class="dialog-actions pa-6 pt-0">
        <v-spacer></v-spacer>

        <v-btn
          variant="text"
          @click="handleClose"
        >
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { Incident, StatusHistory } from '../../../types/incidents'
import websocketService from '@/services/websocket.service'
import { incidentsService } from '@/services/incidentsService'

interface Props {
  modelValue: boolean
  incident?: Incident | null
}

interface TimeSummary {
  status: string
  duration: string
  percentage: number
}

interface HistoryStats {
  totalChanges: number
  uniqueUsers: number
  avgTimeInStatus: string
  totalDuration: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

// Estado del componente
const statusHistory = ref<StatusHistory[]>([])

// Función helper para mapear entradas de historial (evita duplicación)
const mapHistoryEntry = (entry: any, incidentId: number): StatusHistoryEntry => ({
  id: entry.id || String(entry.incidentId),
  incidentId: incidentId,
  status: (entry.status?.toUpperCase() || 'PENDING') as any,
  oldStatus: entry.oldStatus?.toUpperCase() || null,
  timestamp: entry.timestamp,
  userId: entry.userId,
  userName: entry.userName || 'Usuario Desconocido',
  userEmail: entry.userEmail,
  userRole: entry.userRole,
  comment: entry.comment || null,
  automatic: entry.automatic || false
})

// Computed
const localShow = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Estado actual basado en el último item del historial (no en props)
const currentStatus = computed(() => {
  if (statusHistory.value.length === 0) {
    return props.incident?.status || 'PENDING'
  }
  // El último item del historial es el estado más reciente
  const sorted = [...statusHistory.value].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )
  return sorted[0]?.status || props.incident?.status || 'PENDING'
})

const sortedHistory = computed(() => {
  return [...statusHistory.value].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )
})

const historyStats = computed((): HistoryStats => {
  const totalChanges = statusHistory.value.length
  const uniqueUsers = new Set(statusHistory.value.map(h => h.userId)).size
  const totalDurationMs = calculateTotalDuration()
  
  return {
    totalChanges,
    uniqueUsers,
    avgTimeInStatus: formatDuration(totalDurationMs / Math.max(totalChanges - 1, 1)),
    totalDuration: formatDuration(totalDurationMs)
  }
})

const timeSummary = computed((): TimeSummary[] => {
  const statusDurations = new Map<string, number>()
  const sorted = [...statusHistory.value]
    .filter(h => h.timestamp && !isNaN(new Date(h.timestamp).getTime()))
    .sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  
  if (sorted.length === 0) return []
  
  const totalDuration = calculateTotalDuration()
  
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i]
    const next = sorted[i + 1]
    const currentTime = new Date(current.timestamp).getTime()
    const nextTime = new Date(next.timestamp).getTime()
    
    if (!isNaN(currentTime) && !isNaN(nextTime)) {
      const duration = nextTime - currentTime
      statusDurations.set(current.status, (statusDurations.get(current.status) || 0) + duration)
    }
  }
  
  // Agregar tiempo del estado actual
  if (sorted.length > 0) {
    const lastStatus = sorted[sorted.length - 1]
    const lastTime = new Date(lastStatus.timestamp).getTime()
    if (!isNaN(lastTime)) {
      const currentDuration = Date.now() - lastTime
      statusDurations.set(lastStatus.status, (statusDurations.get(lastStatus.status) || 0) + currentDuration)
    }
  }
  
  return Array.from(statusDurations.entries())
    .map(([status, duration]) => ({
      status,
      duration: formatDuration(duration),
      percentage: totalDuration > 0 ? (duration / totalDuration) * 100 : 0
    }))
    .sort((a, b) => b.percentage - a.percentage)
})

// Métodos
const loadStatusHistory = async () => {
  if (!props.incident) {
    console.warn('⚠️ No hay incidencia para cargar historial')
    return
  }
  
  try {
    // Cache buster para forzar request fresco
    const cacheBuster = Date.now()
    const freshIncident = await incidentsService.getIncidentById(props.incident.id, { cacheBuster })
    
    // P13-V6B@V1 HISTORY_TRUTH = CANONICAL_READBACK — el historial visible es EXACTAMENTE el
    // persistido devuelto por el backend. Cero entradas sintéticas (sin actor 'Sistema' fabricado)
    // y cero fallback a datos de props: un historial vacío o un error se muestran como tales.
    statusHistory.value = Array.isArray(freshIncident.statusHistory)
      ? freshIncident.statusHistory.map(entry => mapHistoryEntry(entry, props.incident!.id))
      : []
  } catch (error) {
    console.error('❌ Error cargando historial desde backend:', error)
    statusHistory.value = []
  }
}

const handleClose = () => {
  localShow.value = false
  emit('close')
}

// P13-V5A-R5: 'exportHistory' y 'printHistory' retirados junto con sus botones. Ambos eran un
// console.log seguido de un TODO — cero exportación, cero impresión, cero endpoint. Se retira
// la promesa; no se implementa la capacidad aquí.

// Métodos auxiliares
const calculateTotalDuration = () => {
  if (statusHistory.value.length === 0) return 0
  
  const sorted = [...statusHistory.value]
    .filter(h => h.timestamp && !isNaN(new Date(h.timestamp).getTime()))
    .sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  
  if (sorted.length === 0) return 0
  
  const firstChange = new Date(sorted[0].timestamp).getTime()
  const lastChange = Date.now()
  
  return lastChange - firstChange
}

const formatDuration = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  } else if (minutes > 0) {
    return `${minutes}m`
  } else {
    return `${seconds}s`
  }
}

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'Fecha no disponible'
  
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Fecha inválida'
  
  return date.toLocaleString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const getTimeFromNow = (dateString: string) => {
  if (!dateString) return 'N/A'
  
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'N/A'
  
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  
  if (diffMs < 0) return 'Futuro'
  
  return formatDuration(diffMs) + ' atrás'
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    PENDING: 'mdi-clock-outline',
    IN_PROGRESS: 'mdi-progress-clock',
    PENDING_VALIDATION: 'mdi-clipboard-check-outline',
    CLOSED: 'mdi-check-all',
    CANCELLED: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help'
}

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    PENDING_VALIDATION: 'Pendiente Validación',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }
  return texts[status] || status
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue && props.incident) {
    loadStatusHistory()
  }
})

// ❌ DESHABILITADO: Causa condición de carrera con WebSocket handler
// Este watch se dispara con props.incident desactualizado (19 entradas antiguas)
// mientras el WebSocket handler ya está cargando los datos frescos desde backend
// watch(() => props.incident?.status, (newStatus, oldStatus) => {
//   if (newStatus && oldStatus && newStatus !== oldStatus && props.modelValue) {
//     console.log('🔄 Detectado cambio de estado en props.incident:', oldStatus, '→', newStatus)
//     // Recargar historial automáticamente
//     setTimeout(() => {
//       loadStatusHistory()
//     }, 300)
//   }
// })

// 🔄 Listener de WebSocket para actualizaciones en tiempo real
const handleStatusChanged = async (data: any) => {
  if (import.meta.env.DEV) {
    console.log('📡 StatusHistoryDialog: WebSocket recibido', { 
      dataId: data.incidenceId, 
      propsId: props.incident?.id,
      match: props.incident && Number(data.incidenceId) === props.incident.id 
    })
  }
  
  if (props.incident && Number(data.incidenceId) === props.incident.id) {
    try {
      // Cache buster para forzar request fresco
      const cacheBuster = Date.now()
      if (import.meta.env.DEV) {
        console.log('🔄 StatusHistoryDialog: Recargando historial con cacheBuster:', cacheBuster)
      }
      const updatedIncident = await incidentsService.getIncidentById(props.incident!.id, { cacheBuster })
      
      // Actualizar historial usando función de mapeo compartida
      if (updatedIncident.statusHistory) {
        statusHistory.value = updatedIncident.statusHistory.map(entry => 
          mapHistoryEntry(entry, props.incident!.id)
        )
      }
    } catch (error) {
      console.error('❌ Error recargando historial:', error)
    }
  }
}

onMounted(() => {
  // Escuchar eventos de cambio de estado
  websocketService.on('incident:status-changed', handleStatusChanged)
  if (import.meta.env.DEV) {
    console.log('👂 StatusHistoryDialog montado - Listener WebSocket registrado')
  }
})

onUnmounted(() => {
  // Limpiar listeners al desmontar
  websocketService.off('incident:status-changed', handleStatusChanged)
  if (import.meta.env.DEV) {
    console.log('🧹 StatusHistoryDialog desmontado - Listener WebSocket removido')
  }
})
</script>

<style scoped>
.status-history-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem 2rem;
  position: sticky;
  top: 0;
  z-index: 10;
}

.dialog-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.dialog-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

/* Información de la incidencia */
.incident-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.incident-title {
  font-size: 1rem;
}

.incident-current-status {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

/* Estadísticas */
.history-stats {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.5rem;
}

/* Timeline */
.timeline-title {
  color: #333;
  font-size: 1.1rem;
}

.custom-timeline {
  padding-left: 2rem;
}

.timeline-item {
  margin-bottom: 2rem;
}

.timeline-card {
  margin-left: 1rem;
  width: 100%;
}

.status-card {
  border-radius: 12px !important;
  border-left: 4px solid currentColor;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.status-change {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.status-chip {
  font-weight: 600;
}

.status-transition {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
}

/* Información del usuario */
.user-info {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.user-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.change-time {
  font-size: 0.8rem;
  color: #666;
}

/* Comentario */
.status-comment {
  display: flex;
  align-items: flex-start;
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border-left: 3px solid #2196f3;
}

.comment-text {
  font-style: italic;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Metadata */
.status-metadata {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.metadata-item {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #666;
}

.metadata-text {
  margin-left: 0.25rem;
}

/* Resumen de tiempos */
.time-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.time-item {
  display: grid;
  grid-template-columns: 150px 100px 1fr;
  align-items: center;
  gap: 1rem;
}

.time-status {
  display: flex;
  justify-content: flex-start;
}

.time-duration {
  font-weight: 600;
  text-align: center;
}

.time-percentage {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.percentage-text {
  font-size: 0.8rem;
  color: #666;
  min-width: 40px;
  text-align: right;
}

/* Timeline personalizado */
:deep(.v-timeline-item__body) {
  width: 100% !important;
  max-width: none !important;
}

:deep(.v-timeline-item__opposite) {
  display: none !important;
}

:deep(.v-timeline-divider__dot) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Responsive */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-title {
    font-size: 1.2rem;
  }
  
  .status-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .time-item {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 0.5rem;
  }
  
  .custom-timeline {
    padding-left: 1rem;
  }
  
  .timeline-card {
    margin-left: 0.5rem;
  }
}

/* Scrollbar personalizado */
.history-container::-webkit-scrollbar {
  width: 6px;
}

.history-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.history-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.history-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Animaciones */
.timeline-item {
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.timeline-item:nth-child(1) { animation-delay: 0.1s; }
.timeline-item:nth-child(2) { animation-delay: 0.2s; }
.timeline-item:nth-child(3) { animation-delay: 0.3s; }
.timeline-item:nth-child(4) { animation-delay: 0.4s; }
.timeline-item:nth-child(5) { animation-delay: 0.5s; }

@keyframes fadeInUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Mejoras de accesibilidad */
:deep(.v-btn:focus-visible) {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}
</style>
